import React, { useContext, useMemo, useState } from "react";
import { Button, Input, Label, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Pagination, Search } from "../../DataTable";
import RevalidacionModal from "./RevalidacionModal";
import useRevalidaciones, { RANGO_DEFAULT, PRESETS_ETA } from "./useRevalidaciones";
import { ESTATUS, PASOS, colorEstatus, semaforoDemoras, fmtNum } from "./revalidacionUtils";

const ITEMS_PER_PAGE = 50;
const DOCS_CLAVE = [
  { tipo: "hbl", label: "HBL" },
  { tipo: "bl", label: "BL" },
  { tipo: "EIR", label: "EIR" },
];

export default function ListadoRevalidaciones() {
  const { user } = useContext(AuthContext);
  const [inicio, setInicio] = useState(RANGO_DEFAULT.inicio);
  const [fin, setFin] = useState(RANGO_DEFAULT.fin);
  const { filas, cargando, actualizaFila } = useRevalidaciones(inicio, fin);

  const [busca, setBusca] = useState("");
  const [fCliente, setFCliente] = useState("");
  const [fNaviera, setFNaviera] = useState("");
  const [fEstatus, setFEstatus] = useState("");
  const [fDemoras, setFDemoras] = useState("");
  const [fAgente, setFAgente] = useState("");
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState(null);
  const [alHistorial, setAlHistorial] = useState(false);

  const puedeEditar = !!(user.contenedoresEdit || user.contenedoresCreate);
  const puedeSubir = !!user.contenedoresCreate;

  const opciones = (campo) => [...new Set(filas.map((f) => f.contenedor[campo]).filter(Boolean))].sort();

  const enriquecidas = useMemo(
    () =>
      filas.map((f) => {
        const r = f.revalidacion || {};
        const sem = semaforoDemoras(f.demoras, r.fechaEIR);
        const cambios = r.cambiosAgente || [];
        const pagosPendientes = cambios.filter((c) => c.pagado !== "Si").length;
        const agente = { porCambiar: r.cambioAgente === "Si", pagoPendiente: pagosPendientes > 0, pagoHecho: cambios.length > 0 && !pagosPendientes };
        return { ...f, r, sem, agente, estatus: f.estatus || "", pasos: PASOS.filter((p) => r[p.campo]).length };
      }),
    [filas]
  );

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return enriquecidas
      .filter((f) => !fCliente || f.contenedor.cliente === fCliente)
      .filter((f) => !fNaviera || f.contenedor.naviera === fNaviera)
      .filter((f) => !fEstatus || (fEstatus === "SIN" ? !f.estatus : fEstatus === "CERRADOS" ? f.cerrada : f.estatus === fEstatus))
      .filter((f) => !soloPendientes || !f.cerrada)
      .filter((f) => !fDemoras || f.sem.color === fDemoras)
      .filter((f) => !fAgente || f.agente[fAgente])
      .filter(
        (f) =>
          !t ||
          [f.contenedor.referencia, f.contenedor.numeroReferencia, f.contenedor.cliente, f.contenedor.naviera, f.r.hbl, f.r.bl, f.r.agenteAduanal, f.r.ejecutivoChina]
            .some((v) => String(v || "").toLowerCase().includes(t))
      )
      .sort((a, b) => String(a.contenedor.eta).localeCompare(String(b.contenedor.eta)));
  }, [enriquecidas, busca, fCliente, fNaviera, fEstatus, fDemoras, fAgente, soloPendientes]);

  const kpi = useMemo(() => {
    const k = { total: filtradas.length, sin: 0, proceso: 0, cerrados: 0, vencidas: 0, porVencer: 0, detenidos: 0, porCambiar: 0, pagoPendiente: 0 };
    for (const f of filtradas) {
      if (f.cerrada) k.cerrados++;
      else if (!f.estatus) k.sin++;
      else if (f.estatus === "DETENIDO") k.detenidos++;
      else k.proceso++;
      if (f.sem.color === "danger") k.vencidas++;
      if (f.sem.color === "warning") k.porVencer++;
      if (f.agente.porCambiar) k.porCambiar++;
      if (f.agente.pagoPendiente) k.pagoPendiente++;
    }
    return k;
  }, [filtradas]);

  const paginaFilas = filtradas.slice((pagina - 1) * ITEMS_PER_PAGE, pagina * ITEMS_PER_PAGE);
  const reset = (fn) => (e) => { fn(e.target.value); setPagina(1); };
  const preset = (p) => { const [a, b] = p.rango(); setInicio(a.format("YYYY-MM-DD")); setFin(b.format("YYYY-MM-DD")); setPagina(1); };

  const excel = () => {
    const datos = filtradas.map((f) => ({
      Referencia: f.contenedor.numeroReferencia, Contenedor: f.contenedor.referencia, Cliente: f.contenedor.cliente,
      Naviera: f.contenedor.naviera, Broker: f.contenedor.broker, ETD: f.contenedor.etd, ETA: f.contenedor.eta,
      HBL: f.r.hbl || "", BL: f.r.bl || "", Shipper: f.r.shipper || "", Consignatario: f.r.consignatario || "",
      Mercancia: f.r.descripcionMercancia || "", Bultos: f.r.bultos || "", Peso: f.r.peso || "", Volumen: f.r.volumen || "",
      Buque: f.r.buque || "", Tamano: f.r.tamano || "", Modalidad: f.r.modalidad || "", AgenteAduanal: f.r.agenteAduanal || "",
      VaACambiarAgente: f.agente.porCambiar ? "Si" : "", PagoCambioAgente: f.agente.pagoPendiente ? "Pendiente" : f.agente.pagoHecho ? "Pagado" : "",
      DiasLibres: f.diasLibres ?? "", Deadline: f.demoras.deadline, DiasDemora: f.demoras.diasDemora,
      ...Object.fromEntries(PASOS.map((p) => [p.label, f.r[p.campo] || ""])),
      Estatus: f.estatus || "Sin iniciar",
    }));
    const cols = Object.keys(datos[0] || { Referencia: "" });
    new ExportJsonExcel({ fileName: "Revalidaciones", datas: [{ sheetData: datos, sheetName: "Revalidaciones", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  if (!user.menuContenedores) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-stamp" /></span>
              Revalidaciones
            </h3>
            <p className="page-subtitle">Seguimiento del proceso de revalidación, documentos y demoras por contenedor</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="warning" href="/PagosNaviera"><i className="fas fa-money-check-alt" /> Pagos a naviera</Button>
            {user.menuAdmin ? (
              <Button size="sm" color="primary" href="/RevalidacionesCobros"><i className="fas fa-file-invoice-dollar" /> Cargos y cobros</Button>
            ) : null}
            <Button size="sm" color="secondary" href="/MenuContenedores"><i className="fas fa-arrow-left" /> Regresar</Button>
            <Button size="sm" onClick={excel} disabled={!filtradas.length}><i className="far fa-file-excel" /> Excel</Button>
          </div>
        </div>

        <div className="rc-presets">
          {PRESETS_ETA.map((p) => {
            const [a, b] = p.rango();
            const activo = a.format("YYYY-MM-DD") === inicio && b.format("YYYY-MM-DD") === fin;
            return <button key={p.label} type="button" className={`rc-preset${activo ? " active" : ""}`} onClick={() => preset(p)}>{p.label}</button>;
          })}
        </div>
        <div className="filter-bar">
          <div><Label>ETA desde</Label><Input bsSize="sm" type="date" value={inicio} onChange={reset(setInicio)} /></div>
          <div><Label>ETA hasta</Label><Input bsSize="sm" type="date" value={fin} onChange={reset(setFin)} /></div>
          <div>
            <Label>Cliente</Label>
            <Input bsSize="sm" type="select" value={fCliente} onChange={reset(setFCliente)}>
              <option value="">Todos</option>{opciones("cliente").map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div>
            <Label>Naviera</Label>
            <Input bsSize="sm" type="select" value={fNaviera} onChange={reset(setFNaviera)}>
              <option value="">Todas</option>{opciones("naviera").map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div>
            <Label>Estatus</Label>
            <Input bsSize="sm" type="select" value={fEstatus} onChange={reset(setFEstatus)}>
              <option value="">Todos</option><option value="SIN">Sin iniciar</option><option value="CERRADOS">Cerrados (entregados / EIR)</option>
              {ESTATUS.map((e) => <option key={e.value}>{e.value}</option>)}
            </Input>
          </div>
          <div>
            <Label>Demoras</Label>
            <Input bsSize="sm" type="select" value={fDemoras} onChange={reset(setFDemoras)}>
              <option value="">Todas</option>
              <option value="danger">Vencidas (siguen corriendo)</option>
              <option value="dark">Con EIR y demora</option>
              <option value="warning">Por vencer (≤ 3 días)</option>
              <option value="success">En tiempo</option>
              <option value="secondary">Sin días libres</option>
            </Input>
          </div>
          <div>
            <Label>Cambio de agente</Label>
            <Input bsSize="sm" type="select" value={fAgente} onChange={reset(setFAgente)}>
              <option value="">Todos</option>
              <option value="porCambiar">Va a cambiar</option>
              <option value="pagoPendiente">Pago a naviera pendiente</option>
              <option value="pagoHecho">Pago a naviera hecho</option>
            </Input>
          </div>
          <div style={{ justifyContent: "flex-end" }}>
            <Label check style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={soloPendientes} onChange={(e) => { setSoloPendientes(e.target.checked); setPagina(1); }} /> Solo pendientes
            </Label>
          </div>
        </div>

        <div className={`rc-kpis rv-kpis${cargando ? " rc-loading" : ""}`}>
          {[
            { label: "Contenedores", valor: kpi.total, icon: "fas fa-box", bg: "#dbeafe", fg: "#1d4ed8" },
            { label: "Sin iniciar", valor: kpi.sin, icon: "far fa-circle", bg: "#f1f5f9", fg: "#475569", filtro: () => setFEstatus("SIN") },
            { label: "En proceso", valor: kpi.proceso, icon: "fas fa-spinner", bg: "#fef3c7", fg: "#b45309" },
            { label: "Cerrados", valor: kpi.cerrados, icon: "fas fa-check-circle", bg: "#dcfce7", fg: "#15803d", filtro: () => setFEstatus("CERRADOS") },
            { label: "Demoras vencidas", valor: kpi.vencidas, icon: "fas fa-exclamation-triangle", bg: "#fee2e2", fg: "#b91c1c", filtro: () => setFDemoras("danger") },
            { label: "Por vencer (≤3 d)", valor: kpi.porVencer, icon: "fas fa-hourglass-half", bg: "#ffedd5", fg: "#c2410c", filtro: () => setFDemoras("warning") },
            { label: "Van a cambiar de agente", valor: kpi.porCambiar, icon: "fas fa-exchange-alt", bg: "#e0e7ff", fg: "#4338ca", filtro: () => setFAgente("porCambiar") },
            { label: "Pago por cambio pendiente", valor: kpi.pagoPendiente, icon: "fas fa-money-check-alt", bg: "#fee2e2", fg: "#b91c1c", filtro: () => setFAgente("pagoPendiente") },
          ].map((k) => (
            <div key={k.label} className={`rc-kpi${k.filtro ? " rv-kpi-click" : ""}`} onClick={() => { if (k.filtro) { k.filtro(); setPagina(1); } }}>
              <div className="rc-kpi-icon" style={{ background: k.bg, color: k.fg }}><i className={k.icon} /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{k.label}</div>
                <div className="rc-kpi-value">{fmtNum(k.valor)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="table-toolbar">
          <Pagination total={filtradas.length} itemsPerPage={ITEMS_PER_PAGE} currentPage={pagina} onPageChange={setPagina} />
          <Search onSearch={(v) => { setBusca(v); setPagina(1); }} />
        </div>

        {cargando ? (
          <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando contenedores…</div></div>
        ) : filtradas.length === 0 ? (
          <div className="rc-state"><i className="fas fa-box-open" /><div>No hay contenedores con estos filtros</div></div>
        ) : (
          <Table size="sm" hover className="table-responsive-xl">
            <thead>
              <tr>
                <th>Referencia</th><th>Contenedor</th><th>Cliente</th><th>Naviera</th><th>ETA</th>
                <th className="td-num">Días libres</th><th>Deadline demoras</th><th>Estatus</th><th>Avance</th>
                <th>Agente aduanal</th><th>Documentos</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paginaFilas.map((f) => (
                <tr key={f.contenedor._id} className="rv-row" onClick={() => setAbierto(f.contenedor._id)}>
                  <td className="td-strong">{f.contenedor.numeroReferencia}</td>
                  <td className="td-strong">{f.contenedor.referencia}</td>
                  <td>{f.contenedor.cliente}</td>
                  <td>{f.contenedor.naviera}</td>
                  <td>{f.contenedor.eta || "—"}</td>
                  <td className="td-num">{f.diasLibres ?? <span className="td-muted">—</span>}</td>
                  <td>
                    <div className="rv-deadline">
                      <span>{f.demoras.deadline || "—"}</span>
                      <span className={`badge-soft badge-soft-${f.sem.color}`}>{f.sem.texto}</span>
                    </div>
                  </td>
                  <td><span className={`badge-soft badge-soft-${colorEstatus(f.estatus)}`}>{f.estatus || "Sin iniciar"}</span></td>
                  <td>
                    <div className="rv-avance" title={`${f.pasos} de ${PASOS.length} pasos`}>
                      {PASOS.map((p) => <span key={p.campo} title={p.label} className={f.r[p.campo] ? "ok" : ""} />)}
                    </div>
                  </td>
                  <td className={f.r.agenteAduanal ? undefined : "td-muted"}>
                    {f.r.agenteAduanal || "—"}
                    {f.agente.porCambiar ? <div><span className="badge-soft badge-soft-warning">Va a cambiar</span></div> : null}
                    {f.agente.pagoPendiente ? <div><span className="badge-soft badge-soft-danger">Pago a naviera pendiente</span></div> : null}
                    {f.agente.pagoHecho ? <div><span className="badge-soft badge-soft-success">Pago a naviera hecho</span></div> : null}
                  </td>
                  <td>
                    <div className="rv-doc-pills">
                      {DOCS_CLAVE.map((d) => (
                        <span key={d.tipo} className={f.contenedor.docs[d.tipo] === "Si" ? "ok" : ""}>{d.label}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Button size="sm" color="info" title={puedeEditar ? "Editar" : "Ver"} onClick={(e) => { e.stopPropagation(); setAbierto(f.contenedor._id); }}>
                      <i className={puedeEditar ? "fas fa-edit" : "fas fa-eye"} />
                    </Button>{" "}
                    <Button size="sm" color="secondary" title="Historial de cambios" onClick={(e) => { e.stopPropagation(); setAlHistorial(true); setAbierto(f.contenedor._id); }}>
                      <i className="fas fa-history" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <RevalidacionModal
        isOpen={!!abierto}
        toggle={() => { setAbierto(null); setAlHistorial(false); }}
        contenedorId={abierto}
        alHistorial={alHistorial}
        modo="datos"
        puedeEditar={puedeEditar}
        puedeSubir={puedeSubir}
        onSaved={actualizaFila}
      />
    </>
  );
}
